import React, { useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import Modal from "common/modal";
import ProductLookup from "common/productLookup";
import { api } from "store/slices/api";
import { toast } from "react-toastify";
import { calcuateLineItemTotal } from "../../utils";
import CreatableSelect from "react-select/creatable";
import { useSelector } from "react-redux";

export const AddProductForm = ({ handleProductLookup }) => {
  const { quoteId, floorId } = useParams();
  const { sub } = useSelector((state) => state.loggedInUser);

  const [showProductLookup, setShowProductLookup] = useState(false);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [product, setProduct] = useState("");
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");

  const { data: quote = {} } = api.useGetPriceQuoteQuery(quoteId);
  const { data: floorProducts = [] } = api.useGetFloorProductsQuery(floorId);
  const { data: favorites = [] } = api.useGetFavoriteProductsQuery(sub);

  const [addProduct] = api.useAddFloorProductsMutation();
  const [getProductPrice] = api.useLazyGetProductPriceQuery();

  const handleAddProduct = () => {
    const { categoryCode, category, salesNumber, productName } = product;
    const maxRank = floorProducts.reduce((max, item) => Math.max(max, item.rank ?? 0), 0);
    const totals = calcuateLineItemTotal({ price, quantity, taxRate: quote.taxRate });

    const payload = {
      ...totals,
      productName,
      oldPrice: totals.price,
      categoryCode: categoryCode === "" || categoryCode == null ? null : categoryCode,
      category: category === "" || category == null ? null : category,
      salesNumber: salesNumber === "" || salesNumber == null ? null : salesNumber,
      isTaxChecked: true,
      rank: maxRank + 1,
      floorId,
    };
    const params = { pending: "Adding product", success: "Product added successfully", error: "Failed to add product" };
    toast.promise(addProduct(payload).unwrap(), params);
    setProduct("");
    setPrice("");
    setQuantity("");
    handleProductLookup(false);
  };

  const onProductSelection = async (selected) => {
    setProduct(selected);
    setPrice("");
    setQuantity("");
    setShowProductLookup(false);
    if (selected !== null && quote.prcNumber && selected.salesNumber && quote.accountNumber) {
      setLoadingPrice(true);
      const price = await getProductPrice({
        salesNbr: selected.salesNumber,
        acctNbr: quote.accountNumber,
        prcNbr: quote.prcNumber,
        jobNumber: quote.jobNumber,
      })
        .catch(() => toast.error("Pricing failed for the selected product. Unit price defaulting to zero"))
        .then((res) => res?.data?.priceData?.unitPrice);

      setPrice(price ?? "0.00");
      setLoadingPrice(false);
    }
  };

  const addDisabled = !product?.productName || !quantity || price === "" || loadingPrice;
  return (
    <div className="d-flex flex-wrap mb-2 py-3 px-2 border rounded-1">
      <Form.Group className="me-3" style={{ flexGrow: 3 }}>
        <Form.Label>Product</Form.Label>
        <CreatableSelect
          name="products"
          options={favorites.map((p) => ({ ...p, value: p.salesNumber, label: `${p.salesNumber} - ${p.productName}` }))}
          placeholder="Search your favorites..."
          value={product}
          onChange={onProductSelection}
          getNewOptionData={(value, label) => ({ value, label, productName: value })}
          formatCreateLabel={(inputValue) => `Create manual product "${inputValue}"`}
          isClearable
          autoFocus
        />
      </Form.Group>
      <Form.Group className="me-3">
        <Form.Label>Quantity</Form.Label>
        <Form.Control
          style={{ width: 150 }}
          type="number"
          placeholder="Enter Quantity"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value === "" ? "" : Math.round(e.target.value * 100) / 100)}
        />
      </Form.Group>
      <Form.Group className="me-3">
        <Form.Label>Unit Price</Form.Label>
        <Form.Control
          style={{ width: 150 }}
          type="number"
          placeholder="Enter Price"
          value={price}
          onChange={(e) => {
            setPrice(e.target.value === "" ? "" : Math.round(Math.max(e.target.value, 0) * 100) / 100);
          }}
        />
      </Form.Group>
      <Button className="me-3 align-self-end" style={{ width: 55 }} onClick={handleAddProduct} disabled={addDisabled}>
        {loadingPrice ? <Spinner animation="border" size="sm" /> : "Add"}
      </Button>
      <Button className="me-3 align-self-end" variant="outline-primary" onClick={() => setShowProductLookup(true)}>
        Lookup
      </Button>
      <Button className="align-self-end me-0" variant="outline-primary" onClick={() => handleProductLookup(false)}>
        Close
      </Button>
      {showProductLookup && (
        <Modal
          title="Product Lookup"
          closeButtonText="Close"
          handleCancel={() => setShowProductLookup(false)}
          size="xl"
        >
          <ProductLookup
            onProductSelectionCallback={(product) =>
              onProductSelection({
                ...product,
                id: product.salesNumber,
                label: `${product.salesNumber} - ${product.productName}`,
              })
            }
            isPriceQuote={true}
          />
        </Modal>
      )}
    </div>
  );
};

export default AddProductForm;
